import { AttachmentType, NewAttachment } from "../../views/attachment/AttachmentTypes";

export interface EmailDnsRecord {
    type: string;
    host?: string | null;
    name?: string | null;
    value: string;
    priority?: number | null;
    ttl?: number | string | null;
    original_name?: string | null;
}

export interface EmailDomain {
    id: number;
    created: string;
    modified: string;
    domain: string;
    default_from_prefix: string;
    from_name: string;
    verified: boolean;
    verified_at: string | null;
    dns_records: EmailDnsRecord[];
    provider_domain_id: string | number | null;
    receiving_verified: boolean;
    receiving_verified_at: string | null;
    receiving_dns_records: EmailDnsRecord[];
    receiving_provider_domain_id: string | number | null;
    deleted: boolean;
    division: number;
    division_name: string;
    is_main: boolean;
}

export interface EmailSendingAddress {
    id: number;
    created: string;
    modified: string;
    domain: number;
    domain_name: string;
    prefix: string;
    email: string;
    reply_email: string;
    is_default: boolean;
    deleted: boolean;
    division: number | null;
    division_name: string | null;
}

export interface EmailRecipient {
    id: number;
    created: string;
    modified: string;
    email_message: number;
    email_address: string;
    type: "to" | "cc" | "bcc";
    status: "queued" | "sending" | "sent" | "failed" | "retrying" | "permanent_failure";
    error_message: string | null;
}

export interface EmailMessage {
    id: number | null;
    uuid?: string;
    created: string | null;
    modified: string | null;
    template?: number | null;
    template_name?: string | null;
    layout_id?: number | null;
    recipients: EmailRecipient[] | null;
    to: string;
    bcc: string | null;
    custom_domain: number | null;
    from_name: string;
    from_email: string;
    subject: string | null;
    body: string;
    attachments: AttachmentType[] | null;
    new_attachments?: NewAttachment[] | null;
    add_object_pdf?: boolean;
    sent_at: string | null;
    sent_by?: number | null;
    sent_by_full_name?: string | null;
    error_message: string | null;
    timeline_item: number | null;
    division: number | null;
    deleted: boolean;
}

export const createNewEmailMessage = (uuid: string): EmailMessage => ({
    id: null,
    uuid: uuid,
    created: null,
    modified: null,
    template: null,
    recipients: null,
    to: '',
    bcc: null,
    custom_domain: null,
    from_name: '',
    from_email: '',
    subject: null,
    body: '',
    attachments: null,
    sent_at: null,
    sent_by: null,
    error_message: null,
    timeline_item: null,
    division: null,
    deleted: false
})

export interface EmailReply {
    id: number;
    created: string;
    modified: string;
    message: EmailMessage | null;
    sending_address: number | null;
    sending_address_email: string | null;
    from_email: string;
    from_name: string | null;
    to_email: string | null;
    subject: string | null;
    body: string | null;
    provider_message_id: string | null;
    in_reply_to: string | null;
    is_read: boolean;
    read_at: string | null;
    received_at: string | null;
    timeline_item: number | null;
    forwarded_at: string | null;
    forward_error: string | null;
    division: number | null;
    deleted: boolean;
}

export interface EmailTemplate {
    id?: number;
    name: string;
    template_type: string | null;
    connected_feature: string | null;
    layout: number | null;
    send_to: string[];
    send_to_user_group: number[];
    send_to_specific_email: string;
    from_prefix: string;
    from_email: string;
    custom_domain: number | null;
    sending_address: number | null;
    reply_to?: string | null;
    default_bcc_recipient: string | null;
    subject: string | null;
    body: string;
    default_attachments: AttachmentType[] | null;
    new_default_attachments?: NewAttachment[] | null;
    send_method: string;
    trigger_status: string | null;
    trigger_timing: string | null;
    trigger_offset_value: string | null;
    trigger_offset_unit: string | null;
    appointment_confirmation_button: boolean;
    add_object_pdf: boolean;
    deleted: boolean;
    division: number | null;
};

export const initializeEmailTemplateData = (templateType?: string): EmailTemplate => ({
    name: '',
    template_type: templateType === 'blank' ? null : templateType ?? null,
    connected_feature: null,
    layout: null,
    send_to: [],
    send_to_user_group: [],
    send_to_specific_email: '',
    from_prefix: '',
    from_email: '',
    custom_domain: null,
    sending_address: null,
    reply_to: null,
    default_bcc_recipient: null,
    subject: null,
    body: '',
    default_attachments: null,
    send_method: 'manual',
    trigger_status: null,
    trigger_timing: null,
    trigger_offset_value: null,
    trigger_offset_unit: null,
    appointment_confirmation_button: false,
    add_object_pdf: true,
    deleted: false,
    division: null,
});

export interface EmailLayout {
    id?: number;
    name: string;
    is_default: boolean;
    deleted: boolean;
    division: number | null;
};